import { Trash2, PlusCircle, Edit2, AlertCircle, ChevronDown, ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLoans } from '../hooks/useLoans';
import { formatCurrency } from '../utils/helpers';

export default function LoansPage() {
  const navigate = useNavigate();
  const { loans, loading, addLoan, updateLoan, deleteLoan, getTotalLoans } = useLoans();
  
  const [activeView, setActiveView] = useState('list'); // 'list', 'add', 'edit'
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [expandedLoanId, setExpandedLoanId] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [newLoan, setNewLoan] = useState({
    borrower: '',
    amount: '',
    category: ''
  });

  const [addError, setAddError] = useState('');
  const [editError, setEditError] = useState('');

  const handleAddLoan = async () => {
    setAddError('');
    
    if (!newLoan.borrower || !newLoan.amount || !newLoan.category) {
      setAddError('Please fill required fields');
      return;
    }

    const result = await addLoan({
      borrowerName: newLoan.borrower,
      loanAmount: newLoan.amount,
      notes: newLoan.category
    });

    if (result.success) {
      setActiveView('list');
      setNewLoan({ borrower: '', amount: '', category: '' });
      setAddError('');
    } else {
      setAddError(result.error || 'Failed to add liability');
    }
  };

  const startEditing = (loan) => {
    setSelectedLoan(loan);
    setEditAmount(loan.amount.toString());
    setEditError('');
    setExpandedLoanId(null);
    setActiveView('edit');
  };

  const saveEdit = async () => {
    setEditError('');
    
    const amount = parseFloat(editAmount);
    if (!amount || amount <= 0) {
      setEditError('Please enter a valid amount');
      return;
    }

    const result = await updateLoan(selectedLoan.id, {
      borrowerName: selectedLoan.borrower,
      loanAmount: amount,
      interestRate: selectedLoan.interestRate || 0,
      dueDate: selectedLoan.dueDate,
      notes: selectedLoan.notes || ''
    });

    if (result.success) {
      setActiveView('list');
      setSelectedLoan(null);
      setEditAmount('');
      setEditError('');
    } else {
      setEditError(result.error || 'Failed to update liability');
    }
  };

  const handleDeleteLoan = async (loanId) => {
    await deleteLoan(loanId);
    setExpandedLoanId(null);
  };

  const toggleExpand = (loanId) => {
    setExpandedLoanId(expandedLoanId === loanId ? null : loanId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b141a] flex items-center justify-center">
        <div className="text-gray-400 text-base">Loading...</div>
      </div>
    );
  }

  // Add Liability Form View
  if (activeView === 'add') {
    return (
      <div className="min-h-screen bg-[#0b141a]">
        <div className="bg-[#1f2c34] px-4 py-4 lg:px-6 lg:py-5 flex items-center gap-3 sticky top-0 z-10">
          <button
            onClick={() => {
              setActiveView('list');
              setAddError('');
            }}
            className="text-[#8696a0] p-1 -ml-1 hover:bg-[#2a3942] rounded-full transition-colors"
          >
            <ChevronLeft size={24} className="lg:w-6 lg:h-6" />
          </button>
          <h3 className="text-white text-lg lg:text-2xl font-medium">Add Liability</h3>
        </div>

        <div className="max-w-3xl mx-auto p-4 lg:p-8 pb-20 space-y-4 lg:space-y-6">
          {addError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 lg:p-4 flex items-start gap-2 lg:gap-3">
              <AlertCircle className="w-5 h-5 lg:w-6 lg:h-6 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-300 text-sm lg:text-base flex-1">{addError}</p>
            </div>
          )}

          <div>
            <label className="block text-[#8696a0] text-sm lg:text-base mb-2 lg:mb-3">Category</label>
            <select
              value={newLoan.category}
              onChange={(e) => {
                setNewLoan({ ...newLoan, category: e.target.value });
                if (addError) setAddError('');
              }}
              className="w-full bg-[#2a3942] text-white rounded-lg px-4 py-3 lg:px-5 lg:py-4 text-base lg:text-lg focus:outline-none focus:ring-2 focus:ring-[#00a884] border border-[#2a3942]"
            >
              <option value="">Select category</option>
              <option value="Loan">Loan</option>
              <option value="Outstanding Bill">Outstanding Bill</option>
              <option value="Credit Card">Credit Card</option>
              <option value="Mortgage">Mortgage</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-[#8696a0] text-sm lg:text-base mb-2 lg:mb-3">Liability Name</label>
            <input
              type="text"
              placeholder="e.g. Tala Loan"
              value={newLoan.borrower}
              onChange={(e) => {
                setNewLoan({ ...newLoan, borrower: e.target.value });
                if (addError) setAddError('');
              }}
              className="w-full bg-[#2a3942] text-white placeholder-[#8696a0] rounded-lg px-4 py-3 lg:px-5 lg:py-4 text-base lg:text-lg focus:outline-none focus:ring-2 focus:ring-[#00a884] border border-[#2a3942]"
            />
          </div>

          <div>
            <label className="block text-[#8696a0] text-sm lg:text-base mb-2 lg:mb-3">Amount Owed</label>
            <input
              type="number"
              placeholder="0.00"
              value={newLoan.amount}
              onChange={(e) => {
                setNewLoan({ ...newLoan, amount: e.target.value });
                if (addError) setAddError('');
              }}
              className="w-full bg-[#2a3942] text-white placeholder-[#8696a0] rounded-lg px-4 py-4 lg:px-5 lg:py-5 focus:outline-none focus:ring-2 focus:ring-[#00a884] border border-[#2a3942] text-lg lg:text-2xl"
            />
          </div>

          <button 
            onClick={handleAddLoan} 
            className="w-full bg-[#00a884] text-white rounded-lg px-4 py-3 lg:px-6 lg:py-4 text-base lg:text-lg font-medium active:bg-[#008f6d] hover:bg-[#008f6d] transition-colors mt-6"
          >
            Add Liability
          </button>
        </div>
      </div>
    );
  }

  // Edit Liability Form View
  if (activeView === 'edit' && selectedLoan) {
    return (
      <div className="min-h-screen bg-[#0b141a]">
        <div className="bg-[#1f2c34] px-4 py-4 lg:px-6 lg:py-5 flex items-center gap-3 sticky top-0 z-10">
          <button
            onClick={() => {
              setActiveView('list');
              setSelectedLoan(null);
              setEditAmount('');
              setEditError('');
            }}
            className="text-[#8696a0] p-1 -ml-1 hover:bg-[#2a3942] rounded-full transition-colors"
          >
            <ChevronLeft size={24} className="lg:w-6 lg:h-6" />
          </button>
          <div className="flex-1 min-w-0">
            <h3 className="text-white text-lg lg:text-2xl font-medium">Edit Amount</h3>
            <p className="text-[#8696a0] text-sm lg:text-base truncate">{selectedLoan.borrower}</p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto p-4 lg:p-8 pb-20 space-y-4 lg:space-y-6">
          <div className="bg-[#1f2c34] rounded-lg p-4 lg:p-6">
            <p className="text-[#8696a0] text-xs lg:text-sm mb-2">Current Amount</p>
            <p className="text-red-400 text-xl lg:text-3xl font-medium">{formatCurrency(selectedLoan.amount)}</p>
          </div>

          {editError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 lg:p-4 flex items-start gap-2 lg:gap-3">
              <AlertCircle className="w-5 h-5 lg:w-6 lg:h-6 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-300 text-sm lg:text-base flex-1">{editError}</p>
            </div>
          )}

          <div>
            <label className="block text-[#8696a0] text-sm lg:text-base mb-2 lg:mb-3">New Amount</label>
            <input
              type="number"
              placeholder="0.00"
              value={editAmount}
              onChange={(e) => {
                setEditAmount(e.target.value);
                if (editError) setEditError('');
              }}
              className="w-full bg-[#2a3942] text-white placeholder-[#8696a0] rounded-lg px-4 py-4 lg:px-5 lg:py-5 focus:outline-none focus:ring-2 focus:ring-red-500 border border-[#2a3942] text-lg lg:text-2xl"
            />
          </div>

          {editAmount && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 lg:p-6">
              <p className="text-[#8696a0] text-xs lg:text-sm mb-2">Updated Amount</p>
              <p className="text-red-400 text-xl lg:text-3xl font-medium">
                {formatCurrency(parseFloat(editAmount || 0))}
              </p>
            </div>
          )}

          <button 
            onClick={saveEdit} 
            className="w-full bg-[#00a884] text-white rounded-lg px-4 py-3 lg:px-6 lg:py-4 text-base lg:text-lg font-medium active:bg-[#008f6d] hover:bg-[#008f6d] transition-colors mt-6"
          >
            Save Changes
          </button>
        </div>
      </div>
    );
  }

  // Main List View
  return (
    <div className="min-h-screen bg-[#0b141a]">
      <main className="max-w-7xl mx-auto pb-20 px-4 lg:px-8">

        {/* Header Section */}
        <div className="bg-[#1f2c34] -mx-4 lg:mx-0 lg:rounded-lg lg:mt-6 px-4 lg:px-6 py-4 lg:py-6 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 lg:gap-4 flex-1">
              <button
                onClick={() => navigate('/')}
                className="text-[#8696a0] p-1 -ml-1 active:bg-[#2a3942] hover:bg-[#2a3942] rounded-full transition-colors"
              >
                <ChevronLeft size={24} className="lg:w-6 lg:h-6" />
              </button>
              <div>
                <p className="text-[#8696a0] text-xs lg:text-sm uppercase tracking-wide mb-1">Total Liabilities</p>
                <p className="text-red-400 text-xl lg:text-3xl font-light">{formatCurrency(getTotalLoans())}</p>
              </div>
            </div>
            <button
              onClick={() => {
                setAddError('');
                setActiveView('add');
              }}
              className="bg-[#00a884] text-white w-12 h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center active:bg-[#008f6d] hover:bg-[#008f6d] transition-colors shadow-lg"
            >
              <PlusCircle size={24} className="lg:w-6 lg:h-6" />
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 py-4 lg:py-6">
          <div className="bg-[#1f2c34] rounded-lg p-4 lg:p-5">
            <p className="text-[#8696a0] text-xs lg:text-sm mb-2">Active Liabilities</p>
            <p className="text-white text-lg lg:text-2xl font-medium">{loans.length}</p>
          </div>
          <div className="bg-[#1f2c34] rounded-lg p-4 lg:p-5">
            <p className="text-[#8696a0] text-xs lg:text-sm mb-2">Average Debt</p>
            <p className="text-white text-lg lg:text-2xl font-medium">
              {loans.length > 0 ? formatCurrency(getTotalLoans() / loans.length) : formatCurrency(0)}
            </p>
          </div>
          <div className="bg-[#1f2c34] rounded-lg p-4 lg:p-5 hidden lg:block">
            <p className="text-[#8696a0] text-xs lg:text-sm mb-2">Highest Debt</p>
            <p className="text-red-400 text-lg lg:text-2xl font-medium">
              {loans.length > 0 ? formatCurrency(Math.max(...loans.map(l => l.amount))) : formatCurrency(0)}
            </p>
          </div>
          <div className="bg-[#1f2c34] rounded-lg p-4 lg:p-5 hidden lg:block">
            <p className="text-[#8696a0] text-xs lg:text-sm mb-2">Lowest Debt</p>
            <p className="text-orange-400 text-lg lg:text-2xl font-medium">
              {loans.length > 0 ? formatCurrency(Math.min(...loans.map(l => l.amount))) : formatCurrency(0)}
            </p>
          </div>
        </div>

        {/* Liabilities List */}
        <div className="pt-1">
          {loans.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 lg:gap-4">
              {loans.map((loan) => (
                <div key={loan.id} className="bg-[#1f2c34] rounded-lg overflow-hidden hover:bg-[#253640] transition-colors">
                  {/* Main Info - Tappable to expand */}
                  <button
                    onClick={() => toggleExpand(loan.id)}
                    className="w-full p-4 lg:p-5 text-left active:bg-[#2a3942] transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs lg:text-sm text-orange-400 bg-orange-400/10 px-2 py-1 rounded">
                            {loan.notes || 'Liability'}
                          </span>
                        </div>
                        <h3 className="text-white text-base lg:text-lg font-normal mb-2">{loan.borrower}</h3>
                        <p className="text-red-400 text-lg lg:text-xl font-medium">{formatCurrency(loan.amount)}</p>
                      </div>
                      
                      <ChevronDown 
                        className={`w-5 h-5 lg:w-6 lg:h-6 text-[#8696a0] transition-transform ml-2 flex-shrink-0 ${
                          expandedLoanId === loan.id ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </button>

                  {/* Expanded Actions Menu */}
                  {expandedLoanId === loan.id && (
                    <div className="border-t border-[#2a3942] grid grid-cols-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditing(loan);
                        }}
                        className="py-3 lg:py-4 flex flex-col items-center gap-1 text-blue-400 active:bg-[#2a3942] hover:bg-[#2a3942] transition-colors"
                      >
                        <Edit2 size={18} className="lg:w-5 lg:h-5" />
                        <span className="text-xs lg:text-sm">Edit Amount</span>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteLoan(loan.id);
                        }}
                        className="py-3 lg:py-4 flex flex-col items-center gap-1 text-red-400 active:bg-[#2a3942] hover:bg-[#2a3942] transition-colors border-l border-[#2a3942]"
                      >
                        <Trash2 size={18} className="lg:w-5 lg:h-5" />
                        <span className="text-xs lg:text-sm">Delete</span>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-[#1f2c34] rounded-lg p-8 lg:p-16 text-center mt-4 max-w-2xl mx-auto">
              <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-red-400/10 flex items-center justify-center mx-auto mb-4 lg:mb-6">
                <PlusCircle className="w-8 h-8 lg:w-10 lg:h-10 text-red-400" />
              </div>
              <h3 className="text-white text-base lg:text-2xl font-normal mb-2 lg:mb-3">No Liabilities</h3>
              <p className="text-[#8696a0] text-sm lg:text-base mb-6 lg:mb-8">Track debts, loans, and bills</p>
              <button
                onClick={() => {
                  setAddError('');
                  setActiveView('add');
                }}
                className="bg-[#00a884] text-white px-6 py-3 lg:px-8 lg:py-4 rounded-full inline-flex items-center gap-2 font-medium text-sm lg:text-base active:bg-[#008f6d] hover:bg-[#008f6d] transition-colors"
              >
                <PlusCircle size={18} className="lg:w-5 lg:h-5" />
                Add Liability
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}